import { makeAutoObservable } from "mobx";
import { delay } from "src/common/delay";
import { RemoteData } from "src/common/RemoteData";
import { MultiStepTaskStore } from "src/components/MultiStepTask/MultiStepTaskStore";
import { multiStepOnRowClick } from "src/components/MultiStepTask/utils";
import { TableStore } from "src/components/tables/TableStore";
import { EntityFiltersPageStore } from "src/pages/EntityFiltersPage/EntityFiltersPageStore";
import { ZUid } from "src/types/zUid";
import { ZMc2 } from "../../../Mc2Types";
import { createMc2CopyFromStore } from "./createMc2CopyFromStore";
import { copyMc2Entities } from "../../../apiMc2";

type ParamsMc2CopyFrom = {
  modelObjectId: ZUid;
  dstModelEntityId: ZUid;
  onFinish?: () => void;
};

export class Mc2CopyFromStore {
  constructor() {
    makeAutoObservable(this);
  }

  taskStore = new MultiStepTaskStore();

  srcParams: ParamsMc2CopyFrom = {
    modelObjectId: "",
    dstModelEntityId: "",
  };

  setSrcParams(params: ParamsMc2CopyFrom) {
    this.srcParams = params;
  }

  init(params: ParamsMc2CopyFrom) {
    this.setSrcParams(params);
    this.setStore1(
      new EntityFiltersPageStore({
        objectId: params.modelObjectId,
        selectionSettings: { selectionType: "radio" },
        onRowClick: (row) => multiStepOnRowClick(row, this.store1),
        actions: new Set(), // Чтобы не было кнопки создания модели, т.к. она вызывает переход на другую страницу.
      }),
    );
    this.taskStore.open();
  }

  // --- step 1
  store1: EntityFiltersPageStore | null = null;

  setStore1(st: EntityFiltersPageStore | null) {
    this.store1 = st;
  }

  get srcModelId(): ZUid | undefined {
    return this.store1?.tableStore?.selected[0]?.id;
  }

  get firstDisabled(): boolean {
    return !this.srcModelId;
  }

  // --- step 2
  get selectedMcIds(): ZUid[] {
    return this.store2?.selected.map(({ id }) => id) ?? [];
  }

  get secondDisabled(): boolean {
    return this.selectedMcIds.length === 0;
  }

  store2: TableStore<ZMc2, {}> | null = null;

  setStore2(st: TableStore<ZMc2, {}> | null) {
    this.store2 = st;
  }

  initStep2() {
    const { srcModelId } = this;
    if (srcModelId) {
      this.setStore2(createMc2CopyFromStore(srcModelId));
    }
  }

  // --- copy

  copyInfo: RemoteData<boolean> = { status: "none" };

  setCopyInfo(data: RemoteData<boolean>) {
    this.copyInfo = data;
  }

  async copy() {
    try {
      this.setCopyInfo({ status: "wait" });
      const { dstModelEntityId, onFinish } = this.srcParams;
      const { selectedMcIds, srcModelId } = this;

      if (!srcModelId) throw Error("Не выбрана исходная модель");
      await copyMc2Entities({
        mcEntityIdsFrom: selectedMcIds,
        modelEntityIdsTo: [dstModelEntityId],
      });

      this.setCopyInfo({ status: "ready", result: true });
      onFinish?.();
      await delay(500); // Время для показа сообщения
      this.taskStore.close();
      this.setStore1(null);
      this.setStore2(null);
    } catch (error) {
      this.setCopyInfo({ status: "error", error });
    }
  }
}
